import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Monitor, RefreshCw, Save } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { printerFilterService, PrinterFilterOverview } from '../../../../services/printerFilterService';
import { userProfileService } from '../../../../services/userProfileService';
import type { UserProfileDto } from '../../../../types/userProfile';
import type { Printer, Computer } from '../../../../types/printNode';

interface PrinterAccessPanelProps {
  companyNo: string;
}

type IdSet = Set<number>;

const sameSet = (a: IdSet, b: IdSet) => a.size === b.size && Array.from(a).every(id => b.has(id));

/**
 * TW 36430601: «Skrivertilgang» — hvilke PrintNode-skrivere tenanten, hvert lager og hver profil
 * ser. Tre nivåer med arv: et nivå uten avkrysninger ser alt fra nivået over; når et nivå har
 * avkrysninger, ser det bare dem. Lager velger blant tenantens skrivere, profil blant lagerets.
 * Lagrede skrivere på pakksone, profil og rapport virker uansett — filteret styrer hva
 * operatøren ser og kan velge i skriverdialogene.
 */
export const PrinterAccessPanel: React.FC<PrinterAccessPanelProps> = ({ companyNo }) => {
  const { t } = useTranslation();
  const { warehouses } = useWarehouse();

  const [overview, setOverview] = useState<PrinterFilterOverview | null>(null);
  const [profiles, setProfiles] = useState<UserProfileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [view, setView] = useState<'matrix' | 'profiles'>('matrix');

  // Redigerbar tilstand + lagret utgangspunkt (for å lagre bare nivåer som er endret)
  const [tenantSel, setTenantSel] = useState<IdSet>(new Set());
  const [tenantNames, setTenantNames] = useState<Record<number, string>>({});
  const [whSel, setWhSel] = useState<Record<string, IdSet>>({});
  const [profileSel, setProfileSel] = useState<Record<string, IdSet>>({});
  const [initial, setInitial] = useState<{
    tenant: IdSet; names: Record<number, string>; wh: Record<string, IdSet>; profile: Record<string, IdSet>;
  } | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<string>('');

  const load = useCallback(async () => {
    if (!companyNo) return;
    setLoading(true);
    setError(null);
    try {
      const [data, profileList] = await Promise.all([
        printerFilterService.getOverview(companyNo),
        userProfileService.getAllProfilesForCustomer().catch(() => [] as UserProfileDto[])
      ]);
      setOverview(data);
      setProfiles(profileList.filter(p => !p.companyNo || p.companyNo === companyNo));

      const tenant: IdSet = new Set();
      const names: Record<number, string> = {};
      const wh: Record<string, IdSet> = {};
      const profile: Record<string, IdSet> = {};
      for (const r of data.rows) {
        if (r.scopeType === 0) {
          tenant.add(r.printNodePrinterId);
          if (r.displayName) names[r.printNodePrinterId] = r.displayName;
        } else if (r.scopeType === 1 && r.companyNo === companyNo && r.warehouseID) {
          (wh[r.warehouseID] ??= new Set()).add(r.printNodePrinterId);
        } else if (r.scopeType === 2 && r.userID) {
          (profile[r.userID] ??= new Set()).add(r.printNodePrinterId);
        }
      }
      setTenantSel(new Set(tenant));
      setTenantNames({ ...names });
      setWhSel(Object.fromEntries(Object.entries(wh).map(([k, v]) => [k, new Set(v)])));
      setProfileSel(Object.fromEntries(Object.entries(profile).map(([k, v]) => [k, new Set(v)])));
      setInitial({ tenant, names, wh, profile });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.printNodePrinters.access.loadFailed', {}, 'Kunne ikke hente skriverfilteret.'));
    } finally {
      setLoading(false);
    }
  }, [companyNo, t]);

  useEffect(() => { load(); }, [load]);

  const printers = overview?.printers ?? [];
  const printerIds = useMemo(() => new Set(printers.map(p => p.id)), [printers]);

  // Skrivere gruppert per PrintNode-maskin — «ett klikk per maskin» ved førstegangsoppsett
  const groups = useMemo(() => {
    const computers = new Map<number, Computer | undefined>((overview?.computers ?? []).map(c => [c.id, c]));
    const map = new Map<number, { computer: Computer | undefined; printers: Printer[] }>();
    for (const p of printers) {
      const key = p.computer?.id ?? p.computerId ?? -1;
      const g = map.get(key) ?? { computer: p.computer ?? computers.get(key), printers: [] };
      g.printers.push(p);
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.computer?.name || '').localeCompare(b.computer?.name || ''));
  }, [printers, overview]);

  // Oppsettet peker på skrivere som ikke lenger finnes i PrintNode — fjernes ved neste lagring
  const missingCount = useMemo(() => {
    const ids = new Set((overview?.rows ?? []).map(r => r.printNodePrinterId));
    return Array.from(ids).filter(id => !printerIds.has(id)).length;
  }, [overview, printerIds]);

  const tenantRestricted = tenantSel.size > 0;
  const inTenant = (id: number) => !tenantRestricted || tenantSel.has(id);

  const toggle = (set: IdSet, ids: number[], on: boolean): IdSet => {
    const next = new Set(set);
    ids.forEach(id => (on ? next.add(id) : next.delete(id)));
    return next;
  };

  const setTenant = (ids: number[], on: boolean) => setTenantSel(prev => toggle(prev, ids, on));
  const setWarehouse = (whId: string, ids: number[], on: boolean) =>
    setWhSel(prev => ({ ...prev, [whId]: toggle(prev[whId] ?? new Set(), ids, on) }));
  const setProfile = (userId: string, ids: number[], on: boolean) =>
    setProfileSel(prev => ({ ...prev, [userId]: toggle(prev[userId] ?? new Set(), ids, on) }));

  const handleSave = async () => {
    if (!initial) return;
    setSaving(true);
    setError(null);
    try {
      // Bare skrivere som finnes i PrintNode lagres — utgåtte ID-er ryddes da bort
      const existing = (s: IdSet) => Array.from(s).filter(id => printerIds.has(id));

      const namesChanged = JSON.stringify(tenantNames) !== JSON.stringify(initial.names);
      if (!sameSet(tenantSel, initial.tenant) || namesChanged || missingCount > 0) {
        await printerFilterService.saveTenant(companyNo,
          existing(tenantSel).map(id => ({ printerId: id, displayName: tenantNames[id]?.trim() || null })));
      }
      for (const w of warehouses) {
        const now = whSel[w.warehouseID] ?? new Set<number>();
        const before = initial.wh[w.warehouseID] ?? new Set<number>();
        if (!sameSet(now, before) || missingCount > 0) {
          await printerFilterService.saveWarehouse(companyNo, w.warehouseID, existing(now));
        }
      }
      const userIds = new Set([...Object.keys(profileSel), ...Object.keys(initial.profile)]);
      for (const userId of Array.from(userIds)) {
        const now = profileSel[userId] ?? new Set<number>();
        const before = initial.profile[userId] ?? new Set<number>();
        if (!sameSet(now, before) || missingCount > 0) {
          await printerFilterService.saveProfile(companyNo, userId, existing(now));
        }
      }
      setSavedAt(new Date());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.printNodePrinters.access.saveFailed', {}, 'Kunne ikke lagre skriverfilteret.'));
    } finally {
      setSaving(false);
    }
  };

  const copyProfileFrom = (fromUserId: string) => {
    if (!selectedProfile || !fromUserId) return;
    setProfileSel(prev => ({ ...prev, [selectedProfile]: new Set(prev[fromUserId] ?? []) }));
  };

  const printerLabel = (p: Printer) => tenantNames[p.id]?.trim() || p.displayName || p.name;
  const cellClass = 'px-2 py-1 text-center';

  const renderMatrix = () => (
    <div className="overflow-auto border border-theme-border-primary rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-theme-bg-secondary sticky top-0">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-theme-text-secondary">
              {t('manager.modules.printNodePrinters.access.printer', {}, 'Skriver')}
            </th>
            <th className="text-left px-2 py-2 font-medium text-theme-text-secondary">
              {t('manager.modules.printNodePrinters.access.displayName', {}, 'Visningsnavn')}
            </th>
            <th className="px-2 py-2 font-medium text-theme-text-secondary">
              {t('manager.modules.printNodePrinters.access.tenantColumn', {}, 'Tenant')}
            </th>
            {warehouses.map(w => (
              <th key={w.warehouseID} className="px-2 py-2 font-medium text-theme-text-secondary whitespace-nowrap">
                {w.warehouseDescr || w.warehouseID}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(group => {
            const ids = group.printers.map(p => p.id);
            const allIn = (set: IdSet | undefined) => ids.every(id => set?.has(id));
            return (
              <React.Fragment key={group.computer?.id ?? 'ukjent'}>
                <tr className="bg-theme-bg-secondary border-t border-theme-border-primary">
                  <td className="px-3 py-1.5 font-semibold text-theme-text-primary" colSpan={2}>
                    <span className="inline-flex items-center gap-2">
                      <Monitor size={14} />
                      {group.computer?.name || t('manager.modules.printNodePrinters.unknownComputer', {}, 'Ukjent PC')}
                      <span className="text-xs font-normal text-theme-text-secondary">
                        {t('manager.modules.printNodePrinters.access.wholeComputer', {}, 'hele maskinen')}
                      </span>
                    </span>
                  </td>
                  <td className={cellClass}>
                    <input type="checkbox" className="w-5 h-5" checked={tenantRestricted && allIn(tenantSel)}
                      onChange={e => setTenant(ids, e.target.checked)} />
                  </td>
                  {warehouses.map(w => (
                    <td key={w.warehouseID} className={cellClass}>
                      <input type="checkbox" className="w-5 h-5" checked={allIn(whSel[w.warehouseID])}
                        disabled={!ids.some(inTenant)}
                        onChange={e => setWarehouse(w.warehouseID, ids.filter(inTenant), e.target.checked)} />
                    </td>
                  ))}
                </tr>
                {group.printers.map(p => (
                  <tr key={p.id} className="border-t border-theme-border-primary">
                    <td className="px-3 py-1.5 text-theme-text-primary">
                      <div>{p.name}</div>
                      <div className="text-xs text-theme-text-secondary">
                        ID {p.id}
                        {tenantRestricted && !tenantSel.has(p.id) && (
                          <span className="ml-2 text-amber-700">
                            {t('manager.modules.printNodePrinters.access.notAssigned', {}, 'ikke fordelt')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="text" value={tenantNames[p.id] ?? ''} placeholder={p.name}
                        onChange={e => setTenantNames(prev => ({ ...prev, [p.id]: e.target.value }))}
                        className="w-full min-w-[140px] px-2 py-1 text-sm border border-theme-border-primary rounded bg-theme-bg-primary text-theme-text-primary" />
                    </td>
                    <td className={cellClass}>
                      <input type="checkbox" className="w-5 h-5" checked={tenantSel.has(p.id)}
                        onChange={e => setTenant([p.id], e.target.checked)} />
                    </td>
                    {warehouses.map(w => (
                      <td key={w.warehouseID} className={cellClass}>
                        <input type="checkbox" className="w-5 h-5" checked={!!whSel[w.warehouseID]?.has(p.id)}
                          disabled={!inTenant(p.id)}
                          onChange={e => setWarehouse(w.warehouseID, [p.id], e.target.checked)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderProfiles = () => {
    const profile = profiles.find(p => p.userID === selectedProfile);
    const whSet = profile?.defaultWarehouse ? whSel[profile.defaultWarehouse] : undefined;
    // Profilen velger blant skriverne på standardlageret (eller tenantens når lageret ikke har utvalg)
    const candidates = printers.filter(p => inTenant(p.id) && (!whSet || whSet.size === 0 || whSet.has(p.id)));
    const sel = profileSel[selectedProfile] ?? new Set<number>();
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <select value={selectedProfile} onChange={e => setSelectedProfile(e.target.value)}
            className="px-3 min-h-[44px] text-sm border border-theme-border-primary rounded-md bg-theme-bg-primary text-theme-text-primary">
            <option value="">{t('manager.modules.printNodePrinters.access.chooseProfile', {}, 'Velg profil…')}</option>
            {profiles.map(p => (
              <option key={p.userID} value={p.userID}>
                {p.profileName || p.userID} ({p.userID}){(profileSel[p.userID]?.size ?? 0) > 0 ? ` · ${profileSel[p.userID].size}` : ''}
              </option>
            ))}
          </select>
          {selectedProfile && (
            <select value="" onChange={e => copyProfileFrom(e.target.value)}
              className="px-3 min-h-[44px] text-sm border border-theme-border-primary rounded-md bg-theme-bg-primary text-theme-text-primary">
              <option value="">{t('manager.modules.printNodePrinters.access.copyFrom', {}, 'Kopier fra profil…')}</option>
              {profiles.filter(p => p.userID !== selectedProfile && (profileSel[p.userID]?.size ?? 0) > 0).map(p => (
                <option key={p.userID} value={p.userID}>{p.profileName || p.userID}</option>
              ))}
            </select>
          )}
        </div>
        {selectedProfile && (
          <>
            <p className="text-sm text-theme-text-secondary">
              {profile?.defaultWarehouse
                ? t('manager.modules.printNodePrinters.access.profileHint', { '0': profile.defaultWarehouse },
                    'Skriverne på standardlageret ({0}). Ingen avkrysning = profilen ser alle skriverne på lageret den står på.')
                : t('manager.modules.printNodePrinters.access.profileHintNoWarehouse', {},
                    'Profilen har ikke standardlager — velg blant tenantens skrivere. Ingen avkrysning = profilen ser alle.')}
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {candidates.map(p => (
                <label key={p.id} className="flex items-center gap-3 px-3 min-h-[44px] border border-theme-border-primary rounded-md cursor-pointer text-theme-text-primary">
                  <input type="checkbox" className="w-5 h-5" checked={sel.has(p.id)}
                    onChange={e => setProfile(selectedProfile, [p.id], e.target.checked)} />
                  <span className="text-sm">{printerLabel(p)}</span>
                  <span className="text-xs text-theme-text-secondary ml-auto">{p.computer?.name}</span>
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      <p className="text-sm text-theme-text-secondary">
        {t('manager.modules.printNodePrinters.access.intro', {},
          'Velg hvilke skrivere som vises i Compass. Et nivå uten avkrysninger ser alle skriverne fra nivået over. Når et nivå har avkrysninger, ser det bare dem. Lagrede skrivere på pakksone, profil og rapport virker uansett.')}
      </p>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}
      {missingCount > 0 && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          {t('manager.modules.printNodePrinters.access.missing', { '0': missingCount },
            '{0} skriver(e) i oppsettet finnes ikke lenger i PrintNode. De fjernes når du lagrer.')}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setView('matrix')}
          className={`px-4 min-h-[44px] text-sm rounded-md border ${view === 'matrix' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-theme-border-primary text-theme-text-primary'}`}>
          {t('manager.modules.printNodePrinters.access.tabWarehouses', {}, 'Tenant og lagre')}
        </button>
        <button onClick={() => setView('profiles')}
          className={`px-4 min-h-[44px] text-sm rounded-md border ${view === 'profiles' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-theme-border-primary text-theme-text-primary'}`}>
          {t('manager.modules.printNodePrinters.access.tabProfiles', {}, 'Profiler')}
        </button>
        <div className="flex-1" />
        {savedAt && (
          <span className="text-xs text-theme-text-secondary">
            {t('manager.modules.printNodePrinters.access.savedAt', { '0': savedAt.toLocaleTimeString('nb-NO') }, 'Lagret {0}')}
          </span>
        )}
        <button onClick={load} disabled={loading || saving}
          className="flex items-center gap-2 px-4 min-h-[44px] text-sm rounded-md border border-theme-border-primary text-theme-text-primary disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {t('manager.modules.printNodePrinters.access.reload', {}, 'Hent fra PrintNode')}
        </button>
        <button onClick={handleSave} disabled={saving || loading || !initial}
          className="flex items-center gap-2 px-4 min-h-[44px] text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          <Save size={14} />
          {saving ? t('common.status.saving', {}, 'Lagrer…') : t('common.buttons.save', {}, 'Lagre')}
        </button>
      </div>

      {loading && !overview && (
        <div className="text-sm text-theme-text-secondary">{t('manager.modules.printNodePrinters.loading', {}, 'Laster skrivere...')}</div>
      )}
      {overview && (view === 'matrix' ? renderMatrix() : renderProfiles())}
    </div>
  );
};
